import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Checkbox
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { apiService } from '../services/api';

const DeleteBanner = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBanners, setSelectedBanners] = useState([]);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [deletingBannerId, setDeletingBannerId] = useState(null);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const isAuth = apiService.isAuthenticated();
    if (!isAuth) {
      setIsAuthorized(false);
      setLoading(false);
      return;
    }

    const adminUser = apiService.getAdminUser();
    if (adminUser && adminUser.id) {
      setIsAuthorized(true);
      fetchAllBanners();
    } else {
      setIsAuthorized(false);
      setLoading(false);
    }
  }, []);

  const fetchAllBanners = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBanners();
      if (response.success && response.data) {
        setBanners(response.data);
        
        if (response.data.length === 0) {
          setAlert({
            severity: 'info',
            message: 'No banners found'
          });
        }
      } else {
        throw new Error(response.message || 'Failed to fetch banners');
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      setAlert({
        severity: 'error',
        message: 'Failed to load banners: ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBanner = (bannerId) => {
    setSelectedBanners((prev) =>
      prev.includes(bannerId)
        ? prev.filter((id) => id !== bannerId)
        : [...prev, bannerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBanners.length === banners.length) {
      setSelectedBanners([]);
    } else {
      setSelectedBanners(banners.map((b) => b.id));
    }
  };

  const handleDeleteClick = (banner) => {
    setDeletingBannerId(banner.id);
    setDeleteDialogOpen(true);
  };

  const handleBatchDelete = () => {
    if (selectedBanners.length === 0) {
      setAlert({
        severity: 'warning',
        message: 'Please select at least one banner to delete'
      });
      return;
    }
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const idsToDelete = deletingBannerId ? [deletingBannerId] : selectedBanners;
      let successCount = 0;
      let failCount = 0;

      for (const bannerId of idsToDelete) {
        try {
          const response = await apiService.deleteBanner(bannerId);
          if (response.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error('Failed to delete banner:', bannerId, error);
          failCount++;
        }
      }

      setAlert({
        severity: failCount > 0 ? 'warning' : 'success',
        message: `Deleted: ${successCount}${failCount > 0 ? `, Failed: ${failCount}` : ''}`
      });

      setDeleteDialogOpen(false);
      setDeletingBannerId(null);
      setSelectedBanners([]);
      
      await fetchAllBanners();
    } catch (error) {
      console.error('Error deleting banners:', error);
      setAlert({
        severity: 'error',
        message: 'Failed to delete banner(s): ' + error.message
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        py: 6,
        textAlign: 'center'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          🔒 Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You must be logged in as an admin to view this section.
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (banners.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No banners found</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {alert && (
        <Alert 
          severity={alert.severity} 
          onClose={() => setAlert(null)}
          sx={{ mb: 3 }}
        >
          {alert.message}
        </Alert>
      )}

      <Alert severity="error" sx={{ mb: 3 }}>
        ⚠️ <strong>Warning!</strong> Deleted banners will be removed from rotation and won't be displayed on the website.
      </Alert>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={handleSelectAll}
          sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)' } }}
        >
          {selectedBanners.length === banners.length ? 'Deselect All' : 'Select All'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          {selectedBanners.length} selected of {banners.length}
        </Typography>
        {selectedBanners.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleBatchDelete}
            disabled={loading}
            sx={{ transition: 'all 0.3s', '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 } }}
          >
            Delete Selected ({selectedBanners.length})
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 2 }}>
        <Table>
          <TableHead sx={{ bgcolor: '#fff3e0' }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedBanners.length > 0 && selectedBanners.length < banners.length}
                  checked={selectedBanners.length === banners.length && banners.length > 0}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>CTA</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banners.map((banner) => (
              <TableRow
                key={banner.id}
                sx={{
                  transition: 'all 0.3s',
                  '&:hover': {
                    bgcolor: '#fff9f9',
                    boxShadow: 1
                  },
                  bgcolor: selectedBanners.includes(banner.id) ? '#ffebee' : 'transparent'
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedBanners.includes(banner.id)}
                    onChange={() => handleSelectBanner(banner.id)}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {banner.id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {banner.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  {banner.image_url && (
                    <Box
                      component="img"
                      src={banner.image_url}
                      alt={banner.title}
                      sx={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover', border: '2px solid #ddd' }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {banner.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {banner.cta || 'N/A'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteClick(banner)}
                    disabled={loading}
                    sx={{
                      transition: 'all 0.3s',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: 3
                      }
                    }}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingBannerId(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f' }}>
          🗑️ Delete Banner(s)
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {deletingBannerId ? (
            <Typography color="text.secondary">
              Are you sure you want to delete this banner? 
              <br /><br />
              The banner will be removed from rotation and <strong>won't be displayed on the website</strong>.
            </Typography>
          ) : (
            <Typography color="text.secondary">
              Are you sure you want to delete <strong>{selectedBanners.length} banner(s)</strong>? 
              <br /><br />
              They will be removed from rotation and <strong>won't be displayed on the website</strong>.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeletingBannerId(null);
            }}
            color="primary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeleteBanner;